import type { ICellProps } from '@creditflowresearch/uilib/Table/DataTable';
import {
  TextCell,
  DateCell,
  BooleanCell,
  NumberCell,
  PercentCell,
  MoneyCell,
  LinkedTextCell,
  DictionaryAbbreviationCell,
} from '@/creditflowresearch/ui/common/components/DataCells';
import type {
  IColumnConfigBase,
  INumberColumnConfig,
  IPercentColumnConfig,
  IMoneyColumnConfig,
  IBooleanColumnConfig,
  IDictionaryColumnConfig,
  IColumnDefinition,
  ColumnDataType,
} from './types';

interface IDefaults {
  width: number,
  minWidth: number,
  maxWidth: number,
}

const buildColumnBase = (config: IColumnConfigBase, dataType: ColumnDataType, defaults: IDefaults) => ({
  name: config.id,
  title: config.shortName,
  tooltipContent: config.fullName,
  dataType,
  width: config.width ?? defaults.width,
  minWidth: config.minWidth ?? defaults.minWidth,
  maxWidth: config.maxWidth ?? defaults.maxWidth,
  sortable: config.sortable ?? false,
  customAlign: config.customAlign,
});

export const makeTextColumn = (config: IColumnConfigBase): IColumnDefinition => ({
  ...buildColumnBase(config, 'text', { width: 100, minWidth: 80, maxWidth: 200 }),
  cellCallback: ({ row }: ICellProps) => {
    return (<TextCell value={ row[config.dataField ?? config.id] } />);
  },
});

export const makeDateColumn = (config: IColumnConfigBase): IColumnDefinition => ({
  ...buildColumnBase(config, 'date', { width: 110, minWidth: 100, maxWidth: 130 }),
  cellCallback: ({ row }: ICellProps) => {
    return (<DateCell value={ row[config.dataField ?? config.id] } />);
  },
});

export const makeBooleanColumn = (config: IBooleanColumnConfig): IColumnDefinition => ({
  ...buildColumnBase(config, 'checkbox', { width: 60, minWidth: 50, maxWidth: 70 }),
  cellCallback: ({ row }: ICellProps) => {
    return (
      <BooleanCell
        value={ row[config.dataField ?? config.id] }
        emptyFalse={ config.emptyFalse }
        nullLabel={ config.nullLabel }
      />
    );
  },
});

export const makeNumberColumn = (config: INumberColumnConfig): IColumnDefinition => ({
  ...buildColumnBase(config, 'number', { width: 70, minWidth: 60, maxWidth: 100 }),
  cellCallback: ({ row }: ICellProps) => {
    return (
      <NumberCell
        value={ row[config.dataField ?? config.id] }
        decimals={ config.decimals }
        rounded={ config.rounded }
        thousandsSeparator={ config.thousandsSeparator }
      />
    );
  },
});

export const makePercentColumn = (config: IPercentColumnConfig): IColumnDefinition => ({
  ...buildColumnBase(config, 'percent', { width: 70, minWidth: 60, maxWidth: 90 }),
  cellCallback: ({ row }: ICellProps) => {
    return (<PercentCell value={ row[config.dataField ?? config.id] } decimals={ config.decimals } />);
  },
});

export const makeMoneyColumn = (config: IMoneyColumnConfig): IColumnDefinition => ({
  ...buildColumnBase(config, 'money', { width: 90, minWidth: 70, maxWidth: 140 }),
  cellCallback: ({ row }: ICellProps) => {
    return (<MoneyCell value={ row[config.dataField ?? config.id] } decimals={ config.decimals } />);
  },
});

export const makeDictionaryColumn = (config: IDictionaryColumnConfig): IColumnDefinition => ({
  ...buildColumnBase(config, 'abbreviation', { width: 80, minWidth: 60, maxWidth: 120 }),
  cellCallback: ({ row }: ICellProps) => {
    return (
      <DictionaryAbbreviationCell
        id={ row[config.dataField ?? config.id] }
        dictionaryKey={ config.dictionaryKey }
        displayField={ config.displayField }
      />
    );
  },
});

export const makeListColumn = (config: IColumnConfigBase): IColumnDefinition => ({
  ...buildColumnBase(config, 'list', { width: 120, minWidth: 80, maxWidth: 200 }),
  cellCallback: ({ row }: ICellProps) => {
    return (<LinkedTextCell items={ row[config.dataField ?? config.id] } />);
  },
});
